import * as React from 'react';

import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { useSnackbar } from 'common/hooks/snackbar';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { useSelectionContext } from 'common/contexts/SelectionContext';
import {
  exportWatchList,
  getExportStatus,
  ExportWatchListResponse,
} from 'services/clients/watchList';
import { useQuery } from '@tanstack/react-query';
import { ExportButton } from 'components/ContainerActions';

export const Export: React.FC = () => {
  const { searchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const [exportId, setExportId] = React.useState<string | null>(null);
  const { selection } = useSelectionContext();
  const displaySnackbar = useSnackbar();

  useQuery(['getExportStatus', exportId], () => getExportStatus(exportId), {
    refetchInterval: exportId ? 2000 : false,
    enabled: !!exportId,
    onSuccess: (data: ExportWatchListResponse) => {
      if (data.status === 'error') {
        displaySnackbar({
          severity: 'error',
          text: 'Error while exporting your containers.',
        });
      }
      if (data.status === 'success') {
        const newWin = window.open(data.fileLink);

        if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
          displaySnackbar({
            severity: 'error',
            text: 'Error: please enable popups for this site in your browser settings.',
          });
        }
      }

      if (['error', 'success'].includes(data.status)) {
        setIsExporting(false);
        setExportId(null);
      }
    },
    onError: () => {},
  });

  const handleOnExport = async () => {
    setIsExporting(true);
    const filters = searchParams;

    if (selection.model === 'page') {
      filters.containerNumbers = selection.containers.map((c) => c.containerNumber);
    }

    displaySnackbar({
      severity: 'info',
      text: 'Processing… Preparing your file for download.',
    });

    try {
      const data = await exportWatchList({ filters: { ...filters, unwatched: false } });
      setExportId(data.id);
    } catch {
      displaySnackbar({
        severity: 'error',
        text: 'Error while exporting your containers.',
      });
      setIsExporting(false);
    }
  };

  return <ExportButton onClick={handleOnExport} loading={isExporting} />;
};
Export.displayName = 'TrackAndTrace.Controls.Actions.Export';
